import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import AutoLoginPage from './pages/AutoLoginPage';
import AdminPage from './pages/AdminPage';
import RoutinePage from './pages/RoutinePage';

// Helper: preserves query params (especially embed=true) across redirects
function usePreservedSearch() {
  const [searchParams] = useSearchParams();
  const embed = searchParams.get('embed');
  return embed === 'true' ? '?embed=true' : '';
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-[--hc-text-muted]">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  const embedSearch = usePreservedSearch();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-[--hc-text-muted]">Loading...</div></div>;
  if (user) {
    if (!user.onboarding_complete) return <Navigate to={`/onboarding${embedSearch}`} replace />;
    return <Navigate to={`/dashboard${embedSearch}`} replace />;
  }
  return children;
}

function OnboardingGuard({ children }) {
  const { user, loading } = useAuth();
  const embedSearch = usePreservedSearch();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-[--hc-text-muted]">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_complete) return <Navigate to={`/dashboard${embedSearch}`} replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  const embedSearch = usePreservedSearch();
  if (user) {
    return user.onboarding_complete
      ? <Navigate to={`/dashboard${embedSearch}`} replace />
      : <Navigate to={`/onboarding${embedSearch}`} replace />;
  }
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/auto-login" element={<AutoLoginPage />} />
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/signup" element={<AuthRoute><SignupPage /></AuthRoute>} />
      <Route path="/onboarding" element={<OnboardingGuard><OnboardingPage /></OnboardingGuard>} />
      <Route path="/admin" element={<ProtectedRoute><Layout><AdminPage /></Layout></ProtectedRoute>} />
      <Route path="/routine" element={<ProtectedRoute><Layout><RoutinePage /></Layout></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><Layout><HistoryPage /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><SettingsPage /></Layout></ProtectedRoute>} />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
